import React, { FC, forwardRef, useState } from 'react';
import clsx from 'clsx';
import ReactDatePicker from 'react-datepicker';
import { CalendarIcon } from '@heroicons/react/outline';
import { format } from 'date-fns';

interface ITableDatePicker {
  isDateRange?: boolean;
  onChange: (date: any) => void;
  isLoading?: boolean;
}

const TableDatePicker: FC<ITableDatePicker> = ({ isDateRange, onChange, isLoading }) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filter, setFilter] = useState<'All' | 'Today' | 'na'>('All');

  const onChangeDate = (date: any) => {
    if (Array.isArray(date)) {
      const [start, end] = date;
      setStartDate(start);
      setEndDate(end);
      onChange([start, end]);
    } else {
      setStartDate(date);
    }

    setFilter('na');
  };

  const handleClearDates = () => {
    if (isDateRange) {
      setStartDate(null);
      setEndDate(null);
      onChange([null, null]);
    } else {
      setStartDate(null);
      onChange(null);
    }

    setFilter('All');
  };

  const handleToday = () => {
    const today = new Date();
    setStartDate(today);
    setEndDate(today);
    onChange([today, today]);
    setFilter('Today');
  };

  const CalendarCustomInput = forwardRef(({ onClick }: any, ref: any) => (
    <div
      className={clsx(
        filter === 'na' ? 'bg-hazel-solid-green-600' : 'bg-hazel-light-green-600',
        'py-2 px-8 rounded-t-lg text-white font-base text-xs uppercase cursor-pointer flex items-center space-x-2'
      )}
      onClick={onClick}
    >
      <CalendarIcon className="h-5 w-5" />{' '}
      {startDate && endDate && filter === 'na' && (
        <span>{`${format(new Date(startDate), 'dd/MM/yyyy')} - ${format(
          new Date(endDate),
          'dd/MM/yyyy'
        )}`}</span>
      )}
    </div>
  ));

  return (
    <div className="flex items-end space-x-2">
      <div className="ml-5 space-x-2 flex items-center">
        <div
          className={clsx(
            filter === 'All' ? 'bg-hazel-solid-green-600' : 'bg-hazel-light-green-600',
            'py-2 px-8 rounded-t-lg text-white font-semibold text-sm uppercase cursor-pointer'
          )}
          onClick={handleClearDates}
        >
          All
        </div>
      </div>

      <div className="ml-5 space-x-2 flex items-center">
        <div
          className={clsx(
            filter === 'Today' ? 'bg-hazel-solid-green-600' : 'bg-hazel-light-green-600',
            'py-2 px-8 rounded-t-lg text-white font-semibold text-sm uppercase cursor-pointer'
          )}
          onClick={handleToday}
        >
          Today
        </div>
      </div>

      <ReactDatePicker
        selected={startDate}
        startDate={startDate}
        endDate={endDate}
        onChange={onChangeDate}
        dateFormat="dd/MM/yyyy"
        selectsRange={isDateRange}
        placeholderText="Select date range"
        customInput={<CalendarCustomInput />}
      />
    </div>
  );
};

export default TableDatePicker;
